import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  useMediaQuery,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  ListItemText as MenuListItemText,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import API from "../api/axios";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import {
  getUser,
  getToken,
  logout,
  hasFullAccess,
  isClient,
} from "../auth/auth";
import { NAVY, NAVY_SOFT, ACCENT } from "../theme";
import logo from "../assets/logo.jpg";
import AiChatWidget from "./AiChatWidget";

const DRAWER_WIDTH = 232;

// Icon + color per notification type (#10).
const notifStyle = (type) => {
  switch (type) {
    case "POSITION_ASSIGNED":
      return { Icon: AssignmentIndOutlinedIcon, color: "#2563eb" };
    case "POSITION_UNASSIGNED":
      return { Icon: PersonRemoveOutlinedIcon, color: "#dc2626" };
    case "POSITION_STATUS":
      return { Icon: PauseCircleOutlineIcon, color: "#d97706" };
    case "CANDIDATE_SUBMITTED":
      return { Icon: PersonAddAlt1OutlinedIcon, color: "#16a34a" };
    case "CANDIDATE_STATUS":
      return { Icon: CheckCircleOutlineIcon, color: "#7c3aed" };
    default:
      return { Icon: NotificationsNoneOutlinedIcon, color: "#64748b" };
  }
};

// Group a notification's timestamp into Today / Yesterday / Earlier (#12).
const timeBucket = (iso) => {
  if (!iso) return "Earlier";
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (d >= startOfToday) return "Today";
  if (d >= startOfYesterday) return "Yesterday";
  return "Earlier";
};

// Maps a route to the page name shown in the top-bar breadcrumb.
const pageTitles = {
  "/": "Dashboard",
  "/jobs": "Positions",
  "/candidates": "Candidates",
  "/pipeline": "Pipeline",
  "/my-day": "My Day",
  "/position-drafts": "Position Drafts",
  "/bulk-upload": "Bulk Upload",
  "/users": "Users",
  "/tickets": "Tickets",
  "/settings": "Settings",
  "/client": "Dashboard",
  "/client/positions": "Positions",
  "/client/candidates": "Candidates",
};

const currentPageTitle = (pathname) => {
  if (pageTitles[pathname]) return pageTitles[pathname];
  // fall back to the closest matching prefix
  const match = Object.keys(pageTitles)
    .filter((p) => p !== "/" && p !== "/client" && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return match ? pageTitles[match] : "Portal";
};

const roleLabels = {
  ADMIN: "Administrator",
  MANAGER: "HR Manager",
  RECRUITER: "Recruiter",
  STAFF: "Staff",
};

const baseNav = [
  {
    label: "Dashboard",
    path: "/",
    icon: <DashboardOutlinedIcon />,
    fullOnly: false,
    section: "Overview",
  },
  {
    label: "My Day",
    path: "/my-day",
    icon: <TodayOutlinedIcon />,
    fullOnly: false,
    section: "Overview",
  },
  {
    label: "Positions",
    path: "/jobs",
    icon: <WorkOutlineIcon />,
    fullOnly: false,
    section: "Recruiting",
  },
  {
    label: "Candidates",
    path: "/candidates",
    icon: <PeopleOutlineIcon />,
    fullOnly: false,
    section: "Recruiting",
  },
  {
    label: "Pipeline",
    path: "/pipeline",
    icon: <ViewKanbanOutlinedIcon />,
    fullOnly: false,
    section: "Recruiting",
  },
  {
    label: "Bulk Upload",
    path: "/bulk-upload",
    icon: <UploadFileOutlinedIcon />,
    fullOnly: false,
    section: "Recruiting",
  },
  {
    label: "Clients",
    path: "/clients",
    icon: <BusinessOutlinedIcon />,
    fullOnly: true,
    section: "Recruiting",
  },
  {
    label: "Users",
    path: "/users",
    icon: <GroupOutlinedIcon />,
    fullOnly: true,
    section: "Data & Admin",
  },
  {
    label: "Tickets",
    path: "/tickets",
    icon: <ConfirmationNumberOutlinedIcon />,
    fullOnly: false,
    section: "Data & Admin",
  },
  {
    label: "Settings",
    path: "/settings",
    icon: <SettingsOutlinedIcon />,
    fullOnly: false,
    section: "Data & Admin",
  },
];

const clientNav = [
  {
    label: "Dashboard",
    path: "/client",
    icon: <DashboardOutlinedIcon />,
    fullOnly: false,
  },
  {
    label: "Positions",
    path: "/client/positions",
    icon: <WorkOutlineIcon />,
    fullOnly: false,
  },
  {
    label: "Candidates",
    path: "/client/candidates",
    icon: <PeopleOutlineIcon />,
    fullOnly: false,
  },
  {
    label: "Tickets",
    path: "/tickets",
    icon: <ConfirmationNumberOutlinedIcon />,
    fullOnly: false,
  },
  {
    label: "Settings",
    path: "/settings",
    icon: <SettingsOutlinedIcon />,
    fullOnly: false,
  },
];

export default function AppLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // ── Notifications ──
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [bellAnchor, setBellAnchor] = useState(null);

  const loadNotifs = () => {
    API.get("/notifications")
      .then((r) => {
        const data = r.data || [];
        setNotifs(data);
        setUnread(data.filter((n) => !n.read).length);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadNotifs();
    // poll every 30s so new assignments show without a manual refresh
    const id = setInterval(loadNotifs, 30000);
    return () => clearInterval(id);
  }, []);

  const openBell = (e) => {
    setBellAnchor(e.currentTarget);
  };

  const closeBell = () => setBellAnchor(null);

  const markOneRead = (id) => {
    API.post(`/notifications/${id}/read`).catch(() => {});
    setNotifs((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAllRead = () => {
    API.post("/notifications/read-all").catch(() => {});
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const deleteOne = (id, wasRead) => {
    API.delete(`/notifications/${id}`).catch(() => {});
    setNotifs((prev) => prev.filter((n) => n.id !== id));
    if (!wasRead) setUnread((u) => Math.max(0, u - 1));
  };

  const clearAll = () => {
    API.delete("/notifications").catch(() => {});
    setNotifs([]);
    setUnread(0);
  };

  const onNotifClick = (n) => {
    if (!n.read) markOneRead(n.id);
    closeBell();
    if (n.link) navigate(n.link);
  };
  // On desktop the sidebar can collapse to icons only; mobile always full.
  const drawerWidth = collapsed && !isMobile ? 72 : DRAWER_WIDTH;

  const token = getToken();
  if (!token) return <Navigate to="/login" replace />;

  const user = getUser() || {};
  const fullAccess = hasFullAccess();
  const client = isClient();
  // Clients get a minimal sidebar (just their dashboard); everyone else the staff nav.
  const nav = client
    ? clientNav
    : baseNav.filter((item) => !item.fullOnly || fullAccess);

  const initials = (user.name || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const isActive = (path) =>
    path === "/" || path === "/client"
      ? location.pathname === path
      : location.pathname.startsWith(path);

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: NAVY,
        color: "#fff",
      }}
    >
      <Box
        sx={{
          p: collapsed && !isMobile ? 1.25 : 2,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          justifyContent: collapsed && !isMobile ? "center" : "flex-start",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            objectFit: "cover",
            bgcolor: "#fff",
            flexShrink: 0,
          }}
        />
        {!(collapsed && !isMobile) && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}>
              TechNext
            </Typography>
            <Typography
              sx={{ fontSize: 9, color: "#8fa3c4", letterSpacing: 0.5 }}
            >
              RECRUITMENT PORTAL
            </Typography>
          </Box>
        )}
      </Box>

      {/* Collapse / expand toggle (desktop only) */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            justifyContent: collapsed ? "center" : "flex-end",
            px: 1,
            py: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            sx={{ color: "#c2cee0", "&:hover": { color: "#fff" } }}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}

      <List sx={{ px: 1, py: 1, flex: 1 }}>
        {nav.map((item, index) => {
          const active = isActive(item.path);
          const mini = collapsed && !isMobile;
          const showSectionHeader =
            !mini && item.section && item.section !== nav[index - 1]?.section;
          const button = (
            <ListItemButton
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                borderRadius: 1.25,
                mb: 0.5,
                justifyContent: mini ? "center" : "flex-start",
                px: mini ? 1 : 2,
                color: active ? "#fff" : "#c2cee0",
                bgcolor: active ? ACCENT : "transparent",
                "&:hover": { bgcolor: active ? ACCENT : NAVY_SOFT },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: mini ? 0 : 36 }}>
                {item.icon}
              </ListItemIcon>
              {!mini && (
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                  }}
                  primary={item.label}
                />
              )}
            </ListItemButton>
          );
          // When collapsed, show the label as a tooltip on hover.
          const row = mini ? (
            <Tooltip key={item.path} title={item.label} placement="right">
              {button}
            </Tooltip>
          ) : (
            button
          );
          if (!showSectionHeader) return row;
          return (
            <Box key={`section-${item.path}`}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: "#6b7fa0",
                  textTransform: "uppercase",
                  px: 2,
                  pt: index === 0 ? 0 : 1.5,
                  pb: 0.75,
                }}
              >
                {item.section}
              </Typography>
              {row}
            </Box>
          );
        })}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
      <Box
        sx={{
          p: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          justifyContent: collapsed && !isMobile ? "center" : "flex-start",
        }}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: ACCENT, fontSize: 12 }}>
          {initials}
        </Avatar>
        {!(collapsed && !isMobile) && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.name || "User"}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#8fa3c4" }}>
                {roleLabels[user.role] || user.role}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              sx={{ color: "#c2cee0", "&:hover": { color: "#fff" } }}
              aria-label="Sign out"
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {isMobile && (
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            zIndex: 1300,
            bgcolor: NAVY,
            color: "#fff",
            "&:hover": { bgcolor: NAVY_SOFT },
          }}
          aria-label="Open menu"
        >
          <MenuIcon />
        </IconButton>
      )}

      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: "width 0.2s ease",
        }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              border: "none",
              overflowX: "hidden",
              transition: "width 0.2s ease",
              bgcolor: NAVY,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          maxWidth: "100%",
          overflowX: "hidden",
          transition: "width 0.2s ease",
        }}
      >
        {/* ── Top bar: page title (left) + notification bell/avatar (right) ──
            The page-title lookup (currentPageTitle) already existed in this
            file but was never rendered anywhere — this now surfaces it as a
            simple breadcrumb-style header, matching the redesign direction,
            with no change to how the title is computed. */}
        <Box
          sx={{
            height: 56,
            px: { xs: 2, md: 2.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            bgcolor: "#ffffff",
            borderBottom: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 2px rgba(15,27,51,0.04)",
            position: "sticky",
            top: 0,
            zIndex: 1100,
            pl: { xs: 7, md: 2.5 }, // leave room for mobile menu button
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "-0.2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentPageTitle(location.pathname)}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Tooltip title="Notifications">
              <IconButton size="small" onClick={openBell}>
                <Badge badgeContent={unread} color="error">
                  <NotificationsNoneOutlinedIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            <Avatar
              sx={{ width: 30, height: 30, bgcolor: ACCENT, fontSize: 12 }}
            >
              {initials}
            </Avatar>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                display: { xs: "none", sm: "block" },
              }}
            >
              {user.name || "User"}
            </Typography>
          </Box>

          {/* Notification dropdown panel */}
          <Menu
            anchorEl={bellAnchor}
            open={Boolean(bellAnchor)}
            onClose={closeBell}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { width: 360, maxHeight: 460 } }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: "1px solid #eef1f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                top: 0,
                bgcolor: "#fff",
                zIndex: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Notifications
              </Typography>
              {notifs.length > 0 && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  {unread > 0 && (
                    <Button
                      size="small"
                      onClick={markAllRead}
                      sx={{ fontSize: 11, minWidth: 0, px: 0.5 }}
                    >
                      Mark all read
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="error"
                    onClick={clearAll}
                    sx={{ fontSize: 11, minWidth: 0, px: 0.5 }}
                  >
                    Clear
                  </Button>
                </Box>
              )}
            </Box>

            {notifs.length === 0 ? (
              <Box
                sx={{
                  px: 2,
                  py: 4,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <NotificationsNoneOutlinedIcon
                  sx={{ fontSize: 34, opacity: 0.4, mb: 0.5 }}
                />
                <Typography sx={{ fontSize: 13 }}>
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              (() => {
                // Group notifications by Today / Yesterday / Earlier (#12).
                const groups = { Today: [], Yesterday: [], Earlier: [] };
                notifs.slice(0, 30).forEach((n) => {
                  groups[timeBucket(n.createdAt)].push(n);
                });
                return ["Today", "Yesterday", "Earlier"].map((label) =>
                  groups[label].length === 0 ? null : (
                    <Box key={label}>
                      <Typography
                        sx={{
                          px: 2,
                          py: 0.5,
                          fontSize: 10.5,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          color: "text.secondary",
                          bgcolor: "#f8fafc",
                        }}
                      >
                        {label}
                      </Typography>
                      {groups[label].map((n) => {
                        const { Icon, color } = notifStyle(n.type);
                        return (
                          <MenuItem
                            key={n.id}
                            onClick={() => onNotifClick(n)}
                            sx={{
                              whiteSpace: "normal",
                              alignItems: "flex-start",
                              gap: 1,
                              py: 1,
                              borderBottom: "1px solid #f4f6f9",
                              bgcolor: n.read ? "transparent" : "#eff4ff",
                            }}
                          >
                            <Box
                              sx={{
                                mt: 0.3,
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: `${color}18`,
                                color,
                              }}
                            >
                              <Icon sx={{ fontSize: 16 }} />
                            </Box>
                            <MenuListItemText
                              primary={n.message}
                              secondary={
                                n.createdAt
                                  ? new Date(n.createdAt + "Z").toLocaleString()
                                  : ""
                              }
                              primaryTypographyProps={{
                                fontSize: 13,
                                lineHeight: 1.35,
                                whiteSpace: "normal",
                              }}
                              secondaryTypographyProps={{ fontSize: 11 }}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteOne(n.id, n.read);
                              }}
                              sx={{ mt: 0.2, color: "#94a3b8" }}
                            >
                              <CloseIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  ),
                );
              })()
            )}
          </Menu>
        </Box>

        <Outlet />
        {!client && <AiChatWidget />}
      </Box>
    </Box>
  );
}
